import { Account, AccountStatus } from '@monorepo/types';
import type { StatusTag } from '../types/tag-data.types';

/**
 * Translation function type for dashboardHeader namespace
 */
type TranslationFunction = (key: string) => string;

/**
 * Utility functions for determining account status tags
 */

/**
 * Checks if an account is in a terminated state
 */
export const isAccountTerminated = (accountData: Account): boolean => {
  return !!(
    accountData.state?.terminated ||
    accountData.state?.dailyLoss ||
    accountData.state?.expired
  );
};

/**
 * Checks if an account is in an inactive state
 */
export const isAccountInactive = (accountData: Account): boolean => {
  return !!(
    accountData.status === AccountStatus.Disabled ||
    accountData.status === AccountStatus.PendingCreation ||
    accountData.status === AccountStatus.AwaitingAsyncCreation ||
    accountData.status === AccountStatus.PendingAsyncCreation ||
    accountData.status === AccountStatus.WaitingForRisk ||
    accountData.status === AccountStatus.WaitingForWithdraw ||
    accountData.state?.dailyPause
  );
};

/**
 * Gets the terminated status configuration
 */
export const getTerminatedStatus = (
  accountData: Account,
  t: TranslationFunction,
): StatusTag => {
  let label: string;
  if (accountData.state?.terminated) {
    label = t('tags.status.terminated');
  } else if (accountData.state?.dailyLoss) {
    label = t('tags.status.dailyLoss');
  } else {
    label = t('tags.status.expired');
  }

  return {
    label,
    color: 'error',
    statusDot: 'error',
  };
};

/**
 * Gets the inactive status configuration with appropriate tooltip
 */
export const getInactiveStatus = (
  accountData: Account,
  t: TranslationFunction,
): StatusTag => {
  let tooltip: string;

  if (accountData.state?.dailyPause) {
    tooltip = t('tags.status.tooltips.dailyPause');
  } else if (accountData.status === AccountStatus.Disabled) {
    tooltip = accountData.disableReason || t('tags.status.tooltips.disabled');
  } else if (accountData.status === AccountStatus.PendingCreation) {
    tooltip = t('tags.status.tooltips.pendingCreation');
  } else if (accountData.status === AccountStatus.AwaitingAsyncCreation) {
    tooltip = t('tags.status.tooltips.awaitingAsyncCreation');
  } else if (accountData.status === AccountStatus.PendingAsyncCreation) {
    tooltip = t('tags.status.tooltips.pendingAsyncCreation');
  } else if (accountData.status === AccountStatus.WaitingForRisk) {
    tooltip = t('tags.status.tooltips.waitingForRisk');
  } else if (accountData.status === AccountStatus.WaitingForWithdraw) {
    tooltip = t('tags.status.tooltips.waitingForWithdraw');
  } else {
    tooltip = t('tags.status.tooltips.default');
  }

  return {
    label: t('tags.status.inactive'),
    color: 'textThird',
    statusDot: 'inactive',
    tooltip,
  };
};

/**
 * Gets the active status configuration
 */
export const getActiveStatus = (t: TranslationFunction): StatusTag => {
  return {
    label: t('tags.status.active'),
    color: 'success',
    statusDot: 'success',
  };
};
